/**
 * A time, Danish first (15/10/2026): "05:00 🇩🇰" then "🇦🇺 14:00" quieter —
 * the planned country minute converted for the page's run date (the same
 * conversion as the planner). Without the country's timezone: the country's
 * time alone, as it is.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { cn } from '@/lib/utils';

import { dkTime, shiftMark } from '../../../utils/clock/dk-label';
import { minutesLabel } from '../../../utils/format';
import { useCountryClock } from './country-clock-context';

interface Props {
  /** The country's minute (> 1439 = the next day); null = "—". */
  min: number | null;
  /** The Danish time in bold. */
  strong?: boolean;
  /** Hide the country's echo (a dense list that shows it once elsewhere). */
  dkOnly?: boolean;
  className?: string;
}

export const ClockPair: FC<Props> = ({ min, strong = false, dkOnly = false, className }) => {
  const { tz, flag, runDate } = useCountryClock();
  if (min === null) return <span className={cn('font-mono tabular-nums', className)}>—</span>;
  const dk = dkTime(min, tz, runDate);
  if (!dk) return <span className={cn('font-mono tabular-nums', className)}>{minutesLabel(min)}</span>;
  return (
    <span className={cn('inline-flex items-baseline gap-1 whitespace-nowrap', className)} data-testid="clock-pair">
      <span className={cn('font-mono tabular-nums', strong && 'font-semibold text-foreground')}>{dk.label}</span>
      <span className="text-[10px]" aria-label="Danish time">🇩🇰</span>
      {dk.shift !== 0 ? <span className="text-[10px] text-muted-foreground" title="The Danish date differs from the country's">{shiftMark(dk.shift).trim()}</span> : null}
      {dkOnly ? null : <span className="font-mono text-[11px] tabular-nums text-muted-foreground">· {flag} {minutesLabel(min)}</span>}
    </span>
  );
};
